import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog?published_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${BACKEND_URL}/api/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Error deleting post');
    }
  };

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-unbounded font-black">Blog Posts</h1>
        <Button
          asChild
          className="bg-accent text-white px-6 py-3 font-bold uppercase"
          data-testid="create-post-button"
        >
          <Link to="/admin/blog/new">
            <Plus size={20} className="mr-2" />
            New Post
          </Link>
        </Button>
      </div>

      {loading ? (
        <p className="text-center font-manrope">Loading posts...</p>
      ) : posts.length === 0 ? (
        <div className="bg-card p-12 border border-border text-center">
          <p className="font-manrope text-gray-400">No blog posts yet. Create your first post!</p>
        </div>
      ) : (
        <div className="bg-card border border-border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left font-manrope font-bold">Title</th>
                <th className="px-6 py-4 text-left font-manrope font-bold">Slug</th>
                <th className="px-6 py-4 text-left font-manrope font-bold">Status</th>
                <th className="px-6 py-4 text-left font-manrope font-bold">Date</th>
                <th className="px-6 py-4 text-left font-manrope font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id} className="border-t border-border hover:bg-muted/50" data-testid={`post-row-${post.id}`}>
                  <td className="px-6 py-4 font-manrope">{post.title}</td>
                  <td className="px-6 py-4 font-manrope text-gray-400">{post.slug}</td>
                  <td className="px-6 py-4">
                    <span className={`${post.published ? 'bg-green-500' : 'bg-gray-500'} text-white px-3 py-1 text-xs font-manrope font-medium uppercase`}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-manrope text-gray-400">
                    {new Date(post.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 flex gap-3">
                    <Link
                      to={`/admin/blog/edit/${post.id}`}
                      className="text-accent hover:underline font-manrope font-medium flex items-center gap-1"
                      data-testid={`edit-post-${post.id}`}
                    >
                      <Edit size={16} />
                      Edit
                    </Link>
                    <button
                      onClick={() => handleDelete(post.id)}
                      className="text-red-500 hover:underline font-manrope font-medium flex items-center gap-1"
                      data-testid={`delete-post-${post.id}`}
                    >
                      <Trash size={16} />
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
